/**
 * When the draft preview may be shown on an eviction notice (RAC-10343).
 *
 * RocketLawyer answers "why is the notice being served?" for us at interview
 * creation — the reason radio arrives with "the tenant has failed to pay rent"
 * already true. So from the very first page, before the landlord has typed
 * anything, the preview pane renders a complete, correctly-titled notice: a
 * 14-Day Notice to Pay Rent in New York, a 3-Day Notice to Vacate in Texas, a
 * 5-Day in Illinois. Probed across eight states; all eight did it.
 *
 * That document is a guess. Pick a different reason and it becomes a different
 * instrument with a different clock — Texas flips from a 3-DAY NOTICE TO VACATE
 * to a 30-DAY NOTICE TO TERMINATE TENANCY, and California's options name six
 * distinct notices outright. A landlord ending a month-to-month tenancy is
 * shown, in full and under a watermark that says nothing about which reason it
 * assumed, a demand for rent nobody owes. It is printable from the pane.
 *
 * So the preview is withheld until the reason has actually been answered. This
 * is the display half of the same decision as evictionRadioChoice.js, which
 * blanks the pre-selected radio and refuses to advance past it: there the
 * landlord is stopped from adopting RocketLawyer's guess by accident, here they
 * are stopped from being shown it as fact.
 */

/**
 * @param {boolean} isEviction       is this the eviction flow?
 * @param {boolean} reasonAnswered   has the reason page been submitted? (server-computed)
 * @param {boolean} isTerminalPage   is the wizard on the final review/generate step?
 */
export function shouldShowDraftPreview({ isEviction, reasonAnswered, isTerminalPage }) {
    if (!isEviction) {
        return true;
    }

    if (reasonAnswered === true) {
        return true;
    }

    // The fallback, and the reason a missed match is survivable: by the review
    // step every question has been answered — our own Next gate does not let an
    // eviction radio through unanswered — so the document is the landlord's own
    // whether or not we ever recognized which page asked for the reason. An
    // interview in a state whose wording we do not match therefore loses the
    // preview mid-flow and gets it back at the end, rather than showing a guess.
    return isTerminalPage === true;
}

/**
 * Why the pane is empty, for the landlord. Null when nothing is being withheld.
 *
 * Says what to do rather than what went wrong: an empty pane with no
 * explanation reads as broken, and "we are protecting you from a wrong
 * document" is not information they can act on.
 */
export function withheldPreviewMessage({ isEviction, reasonAnswered, isTerminalPage }) {
    if (shouldShowDraftPreview({ isEviction, reasonAnswered, isTerminalPage })) {
        return null;
    }

    return 'Your notice appears here once you choose the reason for the eviction.'
        + ' The reason decides which notice is built and how long the tenant has to respond.';
}
